import { useState } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { forgotPassword } from "@/lib/api";
import { toast } from "sonner";
import { Lock, Loader2 } from "lucide-react";

const inputCls =
  "rounded-none border-[#12356B]/30 bg-transparent focus-visible:ring-1 focus-visible:ring-[#C0982E] focus-visible:border-[#C0982E] font-mono";

export const LoginGate = () => {
  const { signIn } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);

  const submit = async (e) => {
    e?.preventDefault();
    if (!email.trim() || !password) {
      toast.error("Enter your email and password");
      return;
    }
    setLoading(true);
    try {
      await signIn(email.trim(), password);
      toast.success("Welcome");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Invalid email or password");
    } finally { setLoading(false); }
  };

  const handleForgot = async () => {
    if (!email.trim()) {
      toast.error("Enter your email first, then click “Forgot password?”");
      return;
    }
    setSending(true);
    try {
      await forgotPassword(email.trim());
      toast.success("If that email is an administrator account, a reset link is on its way.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not send reset email");
    } finally { setSending(false); }
  };

  return (
    <div data-testid="login-gate" className="min-h-screen bg-[#12356B] flex flex-col items-center justify-center px-5 relative overflow-hidden">
      <div
        className="absolute inset-0 bg-cover bg-center"
        style={{ backgroundImage: "url('/login-bg.png')" }}
      />
      <div className="absolute inset-0 bg-[#12356B]/70" />
      <div className="absolute inset-0 bg-gradient-to-t from-[#12356B] via-[#12356B]/40 to-transparent" />

      <div className="relative flex flex-col items-center mb-10 text-center">
        <div className="font-serif text-4xl text-[#C0982E] mb-1">eContact</div>
        <div className="font-serif text-2xl text-[#F3F5F8]">North East Fine Arts</div>
        <div className="text-[10px] uppercase tracking-[0.3em] text-[#F3F5F8]/50 mt-0.5">Staff Directory</div>
      </div>

      <div className="relative w-full max-w-sm bg-[#F3F5F8] border border-[#F3F5F8]/10 p-8 shadow-2xl">
        <div className="flex items-center gap-2 mb-1">
          <Lock className="w-4 h-4 text-[#C0982E]" strokeWidth={1.5} />
          <span className="text-[10px] uppercase tracking-[0.25em] text-[#12356B]/50">Private · Members Only</span>
        </div>
        <h1 className="font-serif text-3xl text-[#12356B] mb-6">Sign in to continue</h1>

        <form onSubmit={submit} className="space-y-4">
          <div>
            <Label className="text-xs uppercase tracking-wider">Email</Label>
            <Input data-testid="gate-email" type="email" className={inputCls} value={email} onChange={(e) => setEmail(e.target.value)} autoFocus autoComplete="username" />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider">Password</Label>
            <Input data-testid="gate-password" type="password" className={inputCls} value={password} onChange={(e) => setPassword(e.target.value)} autoComplete="current-password" />
          </div>
          <Button type="submit" data-testid="gate-submit" disabled={loading} className="w-full rounded-none bg-[#12356B] text-[#F3F5F8] hover:bg-[#C0982E] gap-2 uppercase text-xs tracking-wider">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Lock className="w-4 h-4" strokeWidth={1.5} />}
            {loading ? "Signing in…" : "Enter Directory"}
          </Button>
        </form>

        <button
          type="button"
          data-testid="gate-forgot"
          onClick={handleForgot}
          disabled={sending}
          className="mt-4 text-xs text-[#12356B]/55 hover:text-[#C0982E] underline underline-offset-2 disabled:opacity-50"
        >
          {sending ? "Sending reset link…" : "Administrator forgot password?"}
        </button>

        <p className="mt-6 text-[11px] leading-relaxed text-[#12356B]/45">
          Access is by invitation only. If you need an account, contact the directory administrator.
        </p>
      </div>
    </div>
  );
};
